import classnames from 'classnames';
import {
  forwardRef,
  Fragment,
  type ChangeEvent,
  type ComponentType,
  type FocusEvent,
  type MouseEvent,
  type PropsWithChildren,
} from 'react';

export type BaseCheckerInputType = 'checkbox' | 'radio';

export interface BaseCheckerInputProps extends PropsWithChildren {
  id: string | number; // @Todo: Technical debt - add type "number" to keep compatibility with Spartacux usages
  value?: string | number | boolean; // @Todo: Technical debt - add types "number" and "boolean" to keep compatibility with Spartacux usages
  className?: string;
  rootClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  testId?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  type?: BaseCheckerInputType; // The type of checkable.
  tag?: ComponentType<PropsWithChildren<unknown>> | keyof JSX.IntrinsicElements;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => unknown;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => unknown;
  onClick?: (event: MouseEvent<HTMLInputElement>) => unknown;
}

export const BaseCheckerInput = forwardRef<
  HTMLInputElement,
  BaseCheckerInputProps
>(
  (
    {
      id,
      value,
      className,
      rootClassName,
      labelClassName,
      inputClassName,
      testId,
      isChecked = false,
      isDisabled = false,
      isReadOnly = false,
      type = 'checkbox',
      tag: Tag = 'div',
      onChange,
      onBlur,
      onClick,
      children,
      ...props
    },
    ref,
  ) => {
    const handleAction = (
      event:
        | ChangeEvent<HTMLInputElement>
        | FocusEvent<HTMLInputElement>
        | MouseEvent<HTMLInputElement>,
    ) => {
      // Is readonly is not well supported natively
      if (isReadOnly || isDisabled) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }
      switch (event.type) {
        case 'change':
          if (onChange) onChange(event as ChangeEvent<HTMLInputElement>);
          break;
        case 'blur':
          if (onBlur) onBlur(event as FocusEvent<HTMLInputElement>);
          break;
        case 'click':
          if (onClick) onClick(event as MouseEvent<HTMLInputElement>);
          break;
        default:
      }
    };

    return (
      <Tag
        {...(Tag !== Fragment && {
          className: classnames(rootClassName, className),
        })}
      >
        <input
          {...props}
          ref={ref}
          className={inputClassName}
          type={type}
          id={id as string}
          value={value as string}
          checked={isChecked}
          onChange={handleAction}
          onBlur={handleAction}
          onClick={handleAction}
          disabled={isDisabled}
          readOnly={isReadOnly}
          data-testid={testId}
        />
        <label className={labelClassName} htmlFor={id as string}>
          {children}
        </label>
      </Tag>
    );
  },
);

BaseCheckerInput.displayName = 'BaseCheckerInput';
